/**
 * themeDictEs.js — Spanish dictionary for theme chrome (hardcoded JSX strings).
 *
 * This dictionary translates STATIC English strings that live inside theme
 * React components (buttons, labels, placeholders, headings). Dynamic tenant
 * content (catalog items, blog posts, About/Contact body copy) is translated
 * separately via the AI-cached `translations` table.
 *
 * Add new entries as strings are wrapped with `t("...")` in theme files.
 * Keys are the exact English strings, values are the Spanish translations.
 */
const themeDictEs = {
  // ── Buttons / CTAs ───────────────────────────────────────────────
  "Book Now": "Reservar Ahora",
  "BOOK NOW": "RESERVAR AHORA",
  "Book": "Reservar",
  "Book This Item": "Reservar Este Artículo",
  "Book Your Party": "Reserva tu Fiesta",
  "Book Your Bounce House": "Reserva tu Brincolín",
  "Book Your Foam Party": "Reserva tu Fiesta de Espuma",
  "Book online in minutes. Free delivery, setup, and pickup included.": "Reserva en línea en minutos. Entrega, instalación y recogida gratis incluidas.",
  "Book now and let us bring the foam, the fun, and the memories!": "¡Reserva ahora y déjanos traer la espuma, la diversión y los recuerdos!",
  "Shop Now": "Comprar Ahora",
  "Shop All Rentals": "Ver Todas las Rentas",
  "Shop by Category": "Comprar por Categoría",
  "Shop the Deal": "Aprovecha la Oferta",
  "Get Started": "Comenzar",
  "Get Quote": "Obtener Cotización",
  "Get a Quote": "Obtener Cotización",
  "GET A QUOTE": "OBTENER COTIZACIÓN",
  "Get In Touch": "Contáctanos",
  "GET IN TOUCH": "CONTÁCTANOS",
  "Get in Touch": "Contáctanos",
  "Learn More": "Saber Más",
  "View": "Ver",
  "View All": "Ver Todo",
  "View All Packages & Add-Ons": "Ver Todos los Paquetes y Extras",
  "View All Rentals": "Ver Todas las Rentas",
  "View Collection": "Ver Colección",
  "View Details": "Ver Detalles",
  "View Full Inventory": "Ver Inventario Completo",
  "View Packages": "Ver Paquetes",
  "Browse": "Explorar",
  "Browse & Book Now": "Explorar y Reservar",
  "Browse All Rentals": "Ver Todas las Rentas",
  "Browse Catalog": "Ver Catálogo",
  "BROWSE CATALOG": "VER CATÁLOGO",
  "Browse Categories": "Ver Categorías",
  "Browse Collection": "Ver Colección",
  "Browse Our": "Explora Nuestro",
  "Browse Packages": "Ver Paquetes",
  "Browse Rentals": "Ver Rentas",
  "Browse by Category": "Explorar por Categoría",
  "Browse our catalog or get a free quote today.": "Explora nuestro catálogo u obtén una cotización gratis hoy.",
  "Browse our complete collection": "Explora nuestra colección completa",
  "Browse our curated collection of premium event rentals.": "Explora nuestra colección seleccionada de rentas premium para eventos.",
  "Browse our full collection of premium event rental items.": "Explora nuestra colección completa de artículos premium de renta.",
  "Browse our packages, select your preferred booth type, and book in minutes.": "Explora nuestros paquetes, elige tu tipo de cabina y reserva en minutos.",
  "Browse our rentals or get a free quote today.": "Explora nuestras rentas u obtén una cotización gratis hoy.",
  "Explore": "Explorar",
  "Explore Collection": "Explorar Colección",
  "Continue": "Continuar",
  "CONTINUE": "CONTINUAR",
  "Continue to Event Details": "Continuar a Detalles del Evento",
  "CONTINUE TO EVENT DETAILS": "CONTINUAR A DETALLES DEL EVENTO",
  "Review": "Revisar",
  "Review Order": "Revisar Pedido",
  "REVIEW ORDER": "REVISAR PEDIDO",
  "Review Your Booking": "Revisa tu Reserva",
  "Complete Your Booking": "Completa tu Reserva",
  "Back": "Atrás",
  "BACK": "ATRÁS",
  "Back to Home": "Volver al Inicio",
  "Back to Shop": "Volver a la Tienda",
  "Remove": "Eliminar",
  "Select": "Seleccionar",
  "Select type": "Seleccionar tipo",
  "Select event type": "Seleccionar tipo de evento",
  "Select Event Date": "Seleccionar Fecha del Evento",
  "Check Availability": "Consultar Disponibilidad",
  "CHECK": "CONSULTAR",
  "Clear Filters": "Limpiar Filtros",
  "Clear filters": "Limpiar filtros",
  "Sort By": "Ordenar Por",
  "Sort by Name": "Ordenar por Nombre",
  "Name A-Z": "Nombre A-Z",
  "Price: Low-High": "Precio: Bajo a Alto",
  "Price: High-Low": "Precio: Alto a Bajo",
  "Price: Low to High": "Precio: Bajo a Alto",
  "Price: High to Low": "Precio: Alto a Bajo",
  "Search...": "Buscar...",
  "Search rentals...": "Buscar rentas...",
  "Search items...": "Buscar artículos...",
  "Add to Booking": "Añadir a Reserva",
  "Reserve Your Experience": "Reserva tu Experiencia",
  "Reserve your event rentals": "Reserva las rentas para tu evento",

  // ── Navigation / Layout ──────────────────────────────────────────
  "Home": "Inicio",
  "About": "Nosotros",
  "About Us": "Sobre Nosotros",
  "About us": "Sobre nosotros",
  "Contact": "Contacto",
  "Contact Us": "Contáctanos",
  "CONTACT US": "CONTÁCTANOS",
  "Contact Info": "Información de Contacto",
  "Contact Information": "Información de Contacto",
  "FAQ": "Preguntas Frecuentes",
  "Gallery": "Galería",
  "Shop": "Tienda",
  "Rentals": "Rentas",
  "Rental Catalog": "Catálogo de Rentas",
  "Packages": "Paquetes",
  "Packages & Pricing": "Paquetes y Precios",
  "Portfolio": "Portafolio",
  "Categories": "Categorías",
  "CATALOG": "CATÁLOGO",
  "CATEGORIES": "CATEGORÍAS",
  "Call Us": "Llámanos",
  "Call Us Now": "Llámanos Ahora",
  "Sign In": "Iniciar Sesión",
  "Sign In / Register": "Iniciar Sesión / Registrarse",
  "Sign Out": "Cerrar Sesión",
  "My Account": "Mi Cuenta",
  "Quick Links": "Enlaces Rápidos",
  "Help Center": "Centro de Ayuda",
  "HELP CENTER": "CENTRO DE AYUDA",
  "Support": "Soporte",
  "Services": "Servicios",
  "Our Services": "Nuestros Servicios",
  "Service Areas": "Áreas de Servicio",
  "Follow Us": "Síguenos",
  "Newsletter": "Boletín",
  "Join Our Newsletter": "Únete a Nuestro Boletín",
  "Stay Updated": "Mantente Informado",
  "Subscribe": "Suscribirse",
  "Privacy Policy": "Política de Privacidad",
  "Terms & Conditions": "Términos y Condiciones",
  "Powered by": "Desarrollado por",
  "All Rentals": "Todas las Rentas",
  "All Items": "Todos los Artículos",
  "All Types": "Todos los Tipos",
  "All": "Todo",

  // ── Form fields ──────────────────────────────────────────────────
  "Name": "Nombre",
  "Full Name": "Nombre Completo",
  "Last Name": "Apellido",
  "Email": "Correo",
  "Phone": "Teléfono",
  "Company": "Empresa",
  "Message": "Mensaje",
  "Notes": "Notas",
  "Address": "Dirección",
  "Delivery Address": "Dirección de Entrega",
  "Venue Address": "Dirección del Lugar",
  "Event Date": "Fecha del Evento",
  "Event Type": "Tipo de Evento",
  "Event Details": "Detalles del Evento",
  "Event": "Evento",
  "Guest Count": "Número de Invitados",
  "Guests": "Invitados",
  "Estimated guests": "Invitados estimados",
  "Duration": "Duración",
  "Rental Duration": "Duración de la Renta",
  "Your Info": "Tu Información",
  "Your Information": "Tu Información",
  "Your name": "Tu nombre",
  "Your email": "Tu correo",
  "Your email address": "Tu dirección de correo",
  "Special Notes": "Notas Especiales",
  "Setup instructions, access codes, timing preferences...": "Instrucciones de instalación, códigos de acceso, horarios preferidos...",
  "Setup notes, access, timing...": "Notas de instalación, acceso, horarios...",
  "Setup notes, timing, access...": "Notas de instalación, horarios, acceso...",

  // ── Event types ──────────────────────────────────────────────────
  "Wedding": "Boda",
  "Birthday": "Cumpleaños",
  "Birthday Party": "Fiesta de Cumpleaños",
  "Corporate": "Corporativo",
  "Corporate Event": "Evento Corporativo",
  "Graduation": "Graduación",
  "Festival": "Festival",
  "Festival / Fair": "Festival / Feria",
  "Gala / Fundraiser": "Gala / Recaudación",
  "Block Party": "Fiesta de Barrio",
  "Church Event": "Evento de Iglesia",
  "School Event": "Evento Escolar",
  "Other": "Otro",
  "Type": "Tipo",

  // ── Cart / Summary ───────────────────────────────────────────────
  "Summary": "Resumen",
  "Order Summary": "Resumen del Pedido",
  "Your Rental Items": "Tus Artículos de Renta",
  "Your Rentals": "Tus Rentas",
  "Your Items": "Tus Artículos",
  "Items": "Artículos",
  "Quantity": "Cantidad",
  "Qty": "Cant.",
  "Subtotal": "Subtotal",
  "Tax": "Impuesto",
  "Tax (est.)": "Impuesto (est.)",
  "Total": "Total",
  "Estimated": "Estimado",
  "FREE": "GRATIS",
  "Delivery": "Entrega",
  "Delivery & Setup": "Entrega e Instalación",
  "Pickup & Cleanup": "Recogida y Limpieza",
  "No items": "Sin artículos",
  "No items yet": "Aún sin artículos",
  "No items found.": "No se encontraron artículos.",
  "No items found matching your criteria.": "No se encontraron artículos que coincidan con tu búsqueda.",
  "No items in this category yet.": "Aún no hay artículos en esta categoría.",
  "No items match your search": "Ningún artículo coincide con tu búsqueda",
  "No items selected": "Sin artículos seleccionados",
  "No items selected yet": "Aún sin artículos seleccionados",
  "No photos in this category yet": "Aún sin fotos en esta categoría",
  "Empty": "Vacío",
  "Item not found": "Artículo no encontrado",
  "Related Items": "Artículos Relacionados",
  "You May Also Like": "También te Puede Gustar",
  "Specifications": "Especificaciones",

  // ── Badges / Status ──────────────────────────────────────────────
  "NEW": "NUEVO",
  "HOT": "POPULAR",
  "SALE": "OFERTA",
  "BUNDLE DEAL": "PAQUETE",
  "LIMITED OFFER": "OFERTA LIMITADA",
  "SPECIAL OFFER": "OFERTA ESPECIAL",
  "BEST SELLERS": "MÁS VENDIDOS",
  "Best Sellers": "Más Vendidos",
  "Best Selling Products": "Productos Más Vendidos",
  "Most Popular": "Más Populares",
  "Most Booked Items": "Artículos Más Reservados",
  "Popular Picks": "Favoritos",
  "Popular Rentals": "Rentas Populares",
  "Top Picks": "Mejores Opciones",
  "Featured": "Destacados",
  "Featured Categories": "Categorías Destacadas",
  "Latest Products": "Productos Recientes",
  "New Arrivals": "Novedades",
  "Recently": "Recientemente",
  "Recently Viewed": "Vistos Recientemente",
  "Viewed": "Vistos",
  "Available": "Disponible",
  "Booked": "Reservado",
  "Blackout": "Bloqueado",
  "Promo": "Promoción",

  // ── Marketing headlines / sections ───────────────────────────────
  "Our Story": "Nuestra Historia",
  "OUR STORY": "NUESTRA HISTORIA",
  "Our Work": "Nuestro Trabajo",
  "OUR WORK": "NUESTRO TRABAJO",
  "Our work": "Nuestro trabajo",
  "Our Collection": "Nuestra Colección",
  "Our Approach": "Nuestro Enfoque",
  "Our Booths": "Nuestras Cabinas",
  "Our Promise": "Nuestra Promesa",
  "Our Promise to You": "Nuestra Promesa Contigo",
  "Our Values": "Nuestros Valores",
  "Why Choose Us": "¿Por qué Elegirnos?",
  "Why Families Choose Us": "Por qué las Familias nos Eligen",
  "What Sets Us Apart": "Lo que nos Distingue",
  "What Our Clients Say": "Lo que Dicen Nuestros Clientes",
  "Customer Reviews": "Reseñas de Clientes",
  "Testimonials": "Testimonios",
  "The Difference": "La Diferencia",
  "The Party Pop Promise": "La Promesa de Party Pop",
  "Simple Process": "Proceso Simple",
  "Simple, Transparent Pricing": "Precios Simples y Transparentes",
  "How It": "Cómo",
  "Works": "Funciona",
  "Choose Your": "Elige Tu",
  "Choose Your Experience": "Elige tu Experiencia",
  "Choose from our curated packages and book your photo booth experience today.": "Elige entre nuestros paquetes curados y reserva tu experiencia de cabina de fotos hoy.",
  "Choose from our wide selection of party rentals for your next event": "Elige entre nuestra amplia selección de rentas para tu próximo evento",
  "Party Gallery": "Galería de Fiestas",
  "Event Gallery": "Galería de Eventos",
  "Photo Booth Experiences": "Experiencias de Cabina de Fotos",
  "Event Consulting": "Consultoría de Eventos",
  "Custom Packages": "Paquetes Personalizados",
  "Business Hours": "Horario",
  "Hours": "Horario",
  "Party rentals": "Rentas para fiestas",
  "Frequently Asked Questions": "Preguntas Frecuentes",
  "Still Have Questions?": "¿Tienes Más Preguntas?",
  "Find answers to the most common questions about our rental services.": "Encuentra respuestas a las preguntas más comunes sobre nuestros servicios de renta.",

  // ── Hero / CTA sections ──────────────────────────────────────────
  "Ready to Party?": "¿List@ para la Fiesta?",
  "Ready to Plan Your Party?": "¿List@ para Planear tu Fiesta?",
  "Ready to Plan Your Event?": "¿List@ para Planear tu Evento?",
  "Ready to Create Beautiful Memories?": "¿List@ para Crear Bellos Recuerdos?",
  "Ready to Create Something Beautiful?": "¿List@ para Crear Algo Hermoso?",
  "Ready to Create Your Own Memories?": "¿List@ para Crear tus Propios Recuerdos?",
  "Ready to Elevate Your Event?": "¿List@ para Elevar tu Evento?",
  "Ready to Make Your Event Unforgettable?": "¿List@ para Hacer tu Evento Inolvidable?",
  "Ready to Throw the Ultimate Foam Party?": "¿List@ para la Mejor Fiesta de Espuma?",
  "Ready for the": "List@ para la",
  "Let's Plan Your Party": "Planeemos tu Fiesta",
  "Let's Plan Your Foam Party": "Planeemos tu Fiesta de Espuma",
  "Let's Capture Your Event": "Capturemos tu Evento",
  "Let's discuss your event and bring your vision to life.": "Hablemos sobre tu evento y demos vida a tu visión.",
  "See the Fun in": "Mira la Diversión en",
  "See the Fun in Action": "Mira la Diversión en Acción",
  "See the Foam in Action": "Mira la Espuma en Acción",
  "Watch the Fun": "Mira la Diversión",
  "Like What You See?": "¿Te Gusta lo que Ves?",
  "Need Help Choosing?": "¿Necesitas Ayuda Eligiendo?",
  "Need more help?": "¿Necesitas más ayuda?",
  "We'd love to hear from you": "Nos encantaría saber de ti",
  "We'd love to hear from you!": "¡Nos encantaría saber de ti!",
  "We'd love to hear about your upcoming event.": "Nos encantaría saber sobre tu próximo evento.",
  "We're here to help!": "¡Estamos aquí para ayudar!",
  "We're here to help.": "Estamos aquí para ayudar.",
  "Have questions? We're here to help.": "¿Tienes preguntas? Estamos aquí para ayudar.",
  "Have a question? Need a custom quote? We're here to help make your event perfect.": "¿Tienes una pregunta? ¿Necesitas una cotización personalizada? Estamos aquí para hacer tu evento perfecto.",
  "Have questions about packages? Need a custom quote? We'd love to hear from you.": "¿Tienes preguntas sobre paquetes? ¿Necesitas una cotización personalizada? Nos encantaría saber de ti.",
  "Have questions? Need a custom quote? We'd love to hear from you.": "¿Tienes preguntas? ¿Necesitas una cotización personalizada? Nos encantaría saber de ti.",
  "Fill in the details below and we'll prepare your custom quote.": "Completa los datos abajo y prepararemos tu cotización personalizada.",
  "Fill out the form and we'll get back to you ASAP.": "Llena el formulario y te responderemos lo antes posible.",
  "Tell us about your event and we'll get back to you ASAP.": "Cuéntanos sobre tu evento y te responderemos lo antes posible.",
  "Our team is happy to help. Give us a call or send us a message.": "Nuestro equipo está feliz de ayudar. Llámanos o envíanos un mensaje.",
  "Our team is here to help.": "Nuestro equipo está aquí para ayudar.",
  "Our team is here to help. Reach out and we'll get back to you within a few hours.": "Nuestro equipo está aquí para ayudar. Contáctanos y te responderemos en pocas horas.",
  "Our team would love to hear from you.": "A nuestro equipo le encantaría saber de ti.",
  "Our party experts are here to help you plan the perfect event.": "Nuestros expertos en fiestas están aquí para ayudarte a planear el evento perfecto.",
  "We'll respond with a custom quote for your event.": "Te responderemos con una cotización personalizada para tu evento.",
  "We'll Ask You": "Te Preguntaremos",
  "Send Us a Message": "Envíanos un Mensaje",
  "Send a Message": "Enviar Mensaje",
  "Send an Inquiry": "Enviar Consulta",
  "Send Another Message": "Enviar Otro Mensaje",
  "Message Sent": "Mensaje Enviado",
  "Message Sent!": "¡Mensaje Enviado!",
  "Say": "Di",
  "Questions?": "¿Preguntas?",
  "Today!": "¡Hoy!",

  // ── Value propositions ─────────────────────────────────────────
  "Making Every Event Unforgettable": "Hacemos Cada Evento Inolvidable",
  "Making Events Effortless": "Hacemos los Eventos Sencillos",
  "Making every event unforgettable with premium foam party experiences.": "Hacemos cada evento inolvidable con experiencias premium de fiestas de espuma.",
  "Making every party unforgettable with safe, clean, and fun rental equipment.": "Hacemos cada fiesta inolvidable con equipo de renta seguro, limpio y divertido.",
  "Creating Memories": "Creando Recuerdos",
  "Crafted for Moments That Matter": "Hecho para los Momentos que Importan",
  "Capturing Moments,": "Capturando Momentos,",
  "Capturing memories with elegant photo booth experiences for every occasion.": "Capturando recuerdos con experiencias elegantes de cabina de fotos para cada ocasión.",
  "We Bring the Foam, You Bring the Fun": "Nosotros Traemos la Espuma, Tú Traes la Diversión",
  "We Make Parties Awesome!": "¡Hacemos las Fiestas Increíbles!",
  "More Than Just Bounce Houses": "Más que Solo Brincolines",
  "Born From a Love of Foam Parties": "Nacido del Amor por las Fiestas de Espuma",
  "Started With a Single Bounce House": "Comenzó con un Solo Brincolín",
  "Where Elegance Meets Entertainment": "Donde la Elegancia se Encuentra con el Entretenimiento",
  "Premium party and event rental equipment for every celebration.": "Equipo premium de renta para fiestas y eventos para cada celebración.",
  "Premium event rental essentials for weddings, corporate events, and celebrations.": "Elementos premium de renta para bodas, eventos corporativos y celebraciones.",
  "Your one-stop marketplace for party and event rental equipment.": "Tu mercado integral para equipo de renta de fiestas y eventos.",
  "From booking to cleanup, we handle everything so you can focus on having fun!": "Desde la reserva hasta la limpieza, nos encargamos de todo para que te enfoques en divertirte.",
  "Secure your event rentals in a few simple steps.": "Asegura las rentas para tu evento en unos simples pasos.",
  "Safety Information": "Información de Seguridad",
  "Safety": "Seguridad",
  "Sanitized": "Sanitizado",
  "On-Time": "Puntual",
  "Licensed": "Con Licencia",
  "Licensed & Insured": "Con Licencia y Seguro",
  "Complimentary Setup & Delivery": "Instalación y Entrega Cortesía",

  // ── Misc ──────────────────────────────────────────────────────────
  "Date:": "Fecha:",
  "Type:": "Tipo:",
  "Guests:": "Invitados:",
  "Name:": "Nombre:",
  "Email:": "Correo:",
  "Phone:": "Teléfono:",
  "Venue:": "Lugar:",
  "Email address": "Dirección de correo",
  "Connect a tenant": "Conectar un inquilino",
  "Customers": "Clientes",
  "Go": "Ir",
  "Got": "Tengo",
  "Hero": "Portada",
  "Hero video": "Video de portada",
  "Bounce house setup at event": "Instalación de brincolín en el evento",
  "Elegant photo booth setup": "Instalación elegante de cabina de fotos",
  "Event setup": "Instalación del evento",
  "Foam party fun": "Diversión de fiesta de espuma",
  "Foam Level": "Nivel de Espuma",
  "Action!": "¡Acción!",
  "Ultimate Foam Party?": "¿La Mejor Fiesta de Espuma?",
  "Today": "Hoy",
  "Booking": "Reserva",
  "PartyRentalSuite": "PartyRentalSuite",
  "You're on the list!": "¡Estás en la lista!",
  "Your History": "Tu Historial",
  "Get deals & new arrivals": "Recibe ofertas y novedades",
  "Get exclusive deals, new arrivals, and party planning tips.": "Recibe ofertas exclusivas, novedades y consejos para planear fiestas.",
  "Get party tips, exclusive deals, and early access to new rentals!": "Recibe consejos para fiestas, ofertas exclusivas y acceso anticipado a nuevas rentas.",
  "Join our newsletter for deals, new arrivals, and party planning tips.": "Únete a nuestro boletín para recibir ofertas, novedades y consejos para planear fiestas.",
  "Some of our favorite events": "Algunos de nuestros eventos favoritos",
  "Check out some of our awesome events!": "¡Mira algunos de nuestros increíbles eventos!",
  "Real moments from real foam parties. Your kids could be next!": "Momentos reales de fiestas de espuma reales. ¡Tus hijos podrían ser los próximos!",
  "A glimpse into the celebrations we've helped create.": "Un vistazo a las celebraciones que hemos ayudado a crear.",
  "Don't just take our word for it": "No solo creas lo que decimos",
  "Fresh additions to our rental collection": "Nuevas adiciones a nuestra colección de rentas",
  "We've got answers! Here's everything parents ask us about foam parties.": "¡Tenemos respuestas! Esto es todo lo que los padres nos preguntan sobre las fiestas de espuma.",
  "What Our": "Lo que Nuestros",
  "What Parents": "Lo que los Padres",
  "Book your photo booth experience today.": "Reserva tu experiencia de cabina de fotos hoy.",
  "Bundle bounce houses, slides, and party add-ons for the ultimate celebration.": "Combina brincolines, resbaladillas y extras para la mejor celebración.",
  "Outdoor Living": "Vida al Aire Libre",
  "Linens & Draping": "Manteles y Cortinajes",
  "Staging & Lighting": "Escenarios e Iluminación",
  "Tables & Chairs": "Mesas y Sillas",
  "Tents & Canopies": "Carpas y Toldos",
  "Venue Styling": "Diseño del Lugar",

  // ── Marketing — Theme Gallery (DemoThemeGallery.js) ──────────────
  // Theme names
  "Bounce House": "Brincolines",
  "Foam Party": "Fiesta de Espuma",
  "Photo Booth": "Cabina de Fotos",
  "Clean Pro": "Clean Pro",
  "Modern Elegance": "Elegancia Moderna",
  "Bold Shop": "Tienda Audaz",
  "Party Pop": "Party Pop",
  "Mega Rental": "Mega Renta",
  // Theme taglines
  "High-energy fun for bounce house rentals": "Diversión llena de energía para alquiler de brincolines",
  "Vibrant & electric for foam party businesses": "Vibrante y eléctrico para negocios de fiestas de espuma",
  "Elegant & minimal for photo booth services": "Elegante y minimalista para servicios de cabina de fotos",
  "Versatile design for any rental business": "Diseño versátil para cualquier negocio de alquiler",
  "Sophisticated editorial for upscale events": "Diseño editorial sofisticado para eventos exclusivos",
  "Modern e-commerce for any rental catalog": "E-commerce moderno para cualquier catálogo de alquiler",
  "Bright & playful for kids party rentals": "Brillante y divertido para alquileres de fiestas infantiles",
  "Dense marketplace for large catalogs": "Mercado denso para catálogos grandes",
  // Theme feature bullets
  "Navy & orange palette": "Paleta azul marino y naranja",
  "Bold hero sections": "Secciones hero llamativas",
  "Party-ready checkout": "Checkout listo para fiestas",
  "Safety-focused pages": "Páginas enfocadas en seguridad",
  "Blue & cyan gradients": "Degradados azul y cian",
  "Bubble animations": "Animaciones de burbujas",
  "UV glow aesthetic": "Estética con brillo UV",
  "Fun & energetic copy": "Copy divertido y energético",
  "Rose-gold accents": "Acentos oro rosa",
  "Serif typography": "Tipografía serif",
  "Gallery-focused": "Enfocado en galería",
  "Premium & sophisticated": "Premium y sofisticado",
  "Red & dark palette": "Paleta roja y oscura",
  "Professional layout": "Diseño profesional",
  "Multi-category ready": "Listo para multi-categoría",
  "Conversion-optimized": "Optimizado para conversión",
  "Serif headings": "Encabezados serif",
  "Warm neutrals": "Neutros cálidos",
  "Editorial layout": "Diseño editorial",
  "Wedding & gala ready": "Listo para bodas y galas",
  "Dark & green accents": "Acentos oscuros y verdes",
  "Product badges": "Insignias de producto",
  "Star ratings": "Calificaciones con estrellas",
  "Newsletter CTA": "CTA de boletín",
  "Purple & pink gradients": "Degradados morado y rosa",
  "Rounded 16px corners": "Esquinas redondeadas de 16px",
  "Circular categories": "Categorías circulares",
  "Gradient CTAs": "CTAs con degradado",
  "Sidebar categories": "Categorías en barra lateral",
  "Tabbed products": "Productos con pestañas",
  "Dual promo banners": "Banners promocionales dobles",
  "Compact card grid": "Cuadrícula compacta de tarjetas",
  // Dashboard features
  "Full booking management": "Gestión completa de reservas",
  "Calendar with map view": "Calendario con vista de mapa",
  "Customer CRM": "CRM de clientes",
  "Revenue analytics": "Análisis de ingresos",
  "Theme editor": "Editor de temas",
  "Marketing funnels": "Embudos de marketing",
  // Badges
  "Most Popular": "Más Popular",
  "Fan Favorite": "Favorito de los Fans",
  "All-In-One": "Todo en Uno",
  // CTAs / headings
  "Preview Site": "Ver Sitio",
  "Try Dashboard": "Probar Panel",
  "Theme Library": "Biblioteca de Temas",
  "Ready to Launch Your Business?": "¿Listo para Lanzar tu Negocio?",
  "Start Free Trial": "Comenzar Prueba Gratis",
  "Start your free trial — no credit card required.": "Comienza tu prueba gratis — no requiere tarjeta de crédito.",
  "Interactive Demo — No Signup Required": "Demo Interactivo — No se Requiere Registro",
  "Experience the Platform": "Experimenta la Plataforma",
  "Browse our theme library, preview full websites, and explore a live admin dashboard — all pre-loaded with realistic data. See exactly what your business will look like.": "Explora nuestra biblioteca de temas, previsualiza sitios web completos y descubre un panel de administración en vivo — todo precargado con datos realistas. Ve exactamente cómo se verá tu negocio.",
  "Try the Dashboard": "Probar el Panel",
  "Choose Your Look": "Elige tu Estilo",
  "Each theme is a complete, self-contained website with custom pages, checkout flows, and branding.": "Cada tema es un sitio web completo e independiente con páginas personalizadas, flujos de pago y branding.",
  "Full Dashboard Playground": "Zona de Pruebas del Panel Completo",
  "Explore the complete admin experience — bookings, calendar, customers, analytics, theme editor, and more. Pre-loaded with realistic sample data. Resets automatically after 30 minutes.": "Explora la experiencia completa de administración — reservas, calendario, clientes, análisis, editor de temas y más. Precargado con datos de muestra realistas. Se reinicia automáticamente después de 30 minutos.",
  "Launch Playground": "Abrir Zona de Pruebas",
  // Image alt text
  "All-in-one party rental collage with bounce house, tent, foam pit, and photo booth": "Collage de alquiler de fiestas con brincolín, carpa, foso de espuma y cabina de fotos",
  "Colorful bounce house castle with balloons, confetti, and festive decorations": "Castillo brincolín colorido con globos, confeti y decoraciones festivas",
  "Foam party cannon spraying bubbles with happy kids playing in foam": "Cañón de fiesta de espuma lanzando burbujas con niños felices jugando en la espuma",
  "Photo booth setup with golden mirror frame, props, and elegant draping": "Instalación de cabina de fotos con marco dorado, accesorios y cortinajes elegantes",

  // ── Marketing — Comparisons / Competitor (ComparisonsIndex.js + CompetitorComparisonPage.js)
  "Comparisons": "Comparaciones",
  "Comparison Not Found": "Comparación No Encontrada",
  "No comparisons published yet": "Aún no se han publicado comparaciones",
  "Check back soon — we're working on them.": "Vuelve pronto — estamos trabajando en ellas.",
  "Feature": "Característica",
  "Frequently asked": "Preguntas frecuentes",
  "Pricing, compared": "Precios, comparados",
  "See the live demo": "Ver demo en vivo",
  "Start your 14-day trial": "Comienza tu prueba de 14 días",
  "Start your trial": "Comienza tu prueba",
  "Home": "Inicio",
  "Loading…": "Cargando…",
  "Something went wrong": "Algo salió mal",
  "Compare PartyRentalSuite vs Alternatives | Honest SaaS Comparisons": "Compara PartyRentalSuite vs Alternativas | Comparaciones Honestas de SaaS",

  // ── Marketing — Homepage / Pricing / Features (reusable short strings) ──
  "Features": "Características",
  "Pricing": "Precios",
  "Login": "Iniciar Sesión",
  "Live Demo": "Demo en Vivo",
  "Business Types": "Tipos de Negocio",
  "Start 14-day free trial": "Comienza prueba gratis de 14 días",
  "Try the live demo — no signup": "Prueba el demo en vivo — sin registro",
  "Built by rental owners, for rental owners": "Hecho por dueños de alquiler, para dueños de alquiler",
  "How we stack up.": "Así nos comparamos.",
  "Honest, factual comparisons — not hype. We acknowledge where competitors have strengths and tell you where PartyRentalSuite is the right fit.": "Comparaciones honestas y basadas en hechos — sin exageraciones. Reconocemos dónde los competidores tienen fortalezas y te decimos dónde PartyRentalSuite es la opción correcta.",
  "Read comparison": "Leer comparación",
  "Honest Comparison": "Comparación Honesta",
  "Where PartyRentalSuite leads": "Donde PartyRentalSuite lidera",
  "Feature-by-feature": "Característica por característica",
  "Pricing, compared": "Precios, comparados",
  "Switching from": "Migrar desde",
  "Try PartyRentalSuite free for 14 days": "Prueba PartyRentalSuite gratis por 14 días",
  "No credit card required. Import your inventory, or start fresh — either way, you'll be running in under an hour.": "No se requiere tarjeta de crédito. Importa tu inventario o comienza de cero — de cualquier manera, estarás operando en menos de una hora.",
  "Back to Themes": "Volver a Temas",
  "Setting up your playground...": "Configurando tu zona de pruebas...",
  "Applying theme...": "Aplicando tema...",
  "Ready! Launching dashboard...": "¡Listo! Abriendo panel...",
  "Creating demo tenant with sample data": "Creando inquilino de demo con datos de muestra",
  "Customizing for your selected theme": "Personalizando para tu tema seleccionado",
  "Redirecting you now": "Redirigiéndote ahora",
  "Start your trial": "Comienza tu prueba",
  "Start your 14-day trial": "Comienza tu prueba de 14 días",
  "See the live demo": "Ver demo en vivo",
  "All comparisons": "Todas las comparaciones",
};

export default themeDictEs;
